import { loginSchema, type LoginSchema } from "@/api/schemas/auth.schema";
import { Symbol, Wordmark } from "@/components/fornada/brand";
import { TextField } from "@/components/fornada/controls";
import { Display } from "@/components/fornada/primitives";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/auth.provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";

const Copyright = ({ className }: { className?: string }) => (
  <p className={className ?? "text-[11.5px] text-fx-faint"}>
    © {new Date().getFullYear()} Comunidade Católica Apóstolos
  </p>
);

const LoginPage = () => {
  const { signIn, user, isLoading } = useAuth();
  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="grid min-h-screen w-full min-[960px]:grid-cols-2">
      {/* painel de marca (desktop) */}
      <aside className="hidden flex-col border-r border-fx-line-subtle bg-fx-sunken p-10 min-[960px]:flex">
        <Wordmark size={32} />
        <Display
          as="div"
          size="hero"
          className="mt-auto text-[64px] leading-[54px] tracking-[-0.044em]"
        >
          Campanhas
          <br />
          de pizza da
          <br />
          <span className="text-fx-red-icon">Comunidade</span>
        </Display>
        <Copyright className="mt-5 text-xs text-fx-faint" />
      </aside>

      <main className="flex flex-col px-5 pt-6 pb-6 min-[960px]:justify-center min-[960px]:px-12">
        <div className="flex items-center gap-2.5 min-[960px]:hidden">
          <Symbol size={36} />
          <span className="fx-display text-2xl tracking-[-0.025em]">FORNADA</span>
        </div>

        <form
          onSubmit={form.handleSubmit(signIn)}
          className="mx-auto mt-[72px] flex w-full max-w-md flex-1 flex-col gap-3 min-[960px]:mt-0 min-[960px]:flex-none"
        >
          <Display size="hero" className="min-[960px]:text-[32px] min-[960px]:leading-[30px] min-[960px]:tracking-[-0.03em]">
            Acesse
            <br className="min-[960px]:hidden" /> sua conta
          </Display>
          <p className="mb-3 text-sm text-fx-muted">
            Entre para registrar os pedidos da campanha.
          </p>

          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="E-mail"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                placeholder="seu@email.com"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Senha"
                type="password"
                autoComplete="current-password"
                placeholder="Sua senha"
                error={fieldState.error?.message}
              />
            )}
          />

          <Button type="submit" size="xl" className="mt-2 w-full" disabled={isLoading}>
            Entrar
          </Button>

          <p className="mt-1 text-center text-[13px] text-fx-secondary">
            Ainda não tem conta?{" "}
            <Link to="/sign-up" className="font-semibold text-fx-cream hover:text-fx-cream-hover">
              Criar conta
            </Link>
          </p>

          <div className="flex-1 min-[960px]:hidden" />
          <Copyright className="mt-10 text-center text-[11.5px] text-fx-faint min-[960px]:hidden" />
        </form>
      </main>
    </div>
  );
};

export default LoginPage;
